import { QueryClient } from "@tanstack/react-query"
import { ApiError } from "./apiClient"

/**
 * Shared TanStack Query client for the HHU.
 *
 * The HHU is a handheld used on the floor: operators move between scan screens
 * constantly, and several screens re-derive the same facility catalogue data
 * (e.g. the location list, computed from the inventory snapshot). Caching that
 * across screens avoids a full `/inventory` refetch on every navigation.
 *
 * Defaults:
 *  - staleTime 30s: a quick hop between screens reuses data instead of
 *    refetching; long enough to feel instant, short enough to stay current.
 *  - gcTime 5min: keep unused data briefly for back/forward navigation.
 *  - refetchOnWindowFocus off: handhelds blur/focus constantly (scanner, lock
 *    screen) — we don't want a refetch storm. Explicit retry/invalidation only.
 *  - retry once: apiClient already handles 401 refresh + timeouts; one retry
 *    covers a transient blip without hammering a failing endpoint.
 *
 * The three-state loading/empty/error UX (useAsyncData + LoadState) is the
 * HHU's contract and is preserved — query hooks adapt their output to it
 * rather than replacing it.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // 4xx client errors will not resolve on retry — skip them
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
          return failureCount < 1
        },
      },
    },
  })
}

/** Catalogue-like data (locations) changes rarely — reuse a longer TTL. */
export const CATALOGUE_STALE_TIME = 30 * 60_000
